import { all, takeLatest, put, call } from 'redux-saga/effects';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import { t } from 'src/i18n';
import notifications from 'src/redux/notifications/actions';
import { loadCards, loadFormData } from './api';
import { requestCardDelete } from './rateCardsShared/deleteCardRequest';
import { buildListSearch } from './searchParams';
import actions from './actions';
import actionTypes from './actionTypes';
import type { DeleteRequestAction, LoadDataRequestAction } from './actions.d';

export function* loadFormDataRequest(): Generator<any, void, any> {
  try {
    const result = yield call(sendRequest, loadFormData, {}, notifications.REQUEST_ERROR);

    if (result?.status === 200 && result.data?.success?.data?.engines) {
      yield put(actions.loadFormDataSuccess({ engines: result.data.success.data.engines }));

      return;
    }

    yield put(actions.loadFormDataError());
  } catch (error) {
    logger.error(error);
    yield put(actions.loadFormDataError());
  }
}

export function* loadDataRequest({ payload }: LoadDataRequestAction): Generator<any, void, any> {
  try {
    const result = yield call(
      sendRequest,
      loadCards,
      { search: buildListSearch(payload) },
      notifications.REQUEST_ERROR,
    );

    if (result?.status === 200 && result.data?.success?.data?.records) {
      const { records, totalCount, filteredCount, sortableColumns = [], filters = [] } = result.data.success.data;

      yield put(
        actions.loadDataSuccess({
          records,
          total: totalCount,
          filteredTotal: filteredCount,
          filters,
          sortableColumns,
        }),
      );

      return;
    }

    yield put(actions.loadDataError());
  } catch (error) {
    logger.error(error);
    yield put(actions.loadDataError());
  }
}

/**
 * The endpoint re-checks the same precondition under a lock, so a card published since the page loaded
 * is refused, not raced. Refetching is the component's: the query on screen lives in the URL, not here.
 */
export function* deleteCardRequest({ payload }: DeleteRequestAction): Generator<any, void, any> {
  const deleted = yield call(requestCardDelete, payload.id);

  if (!deleted) {
    yield put(actions.deleteError());

    return;
  }

  yield put(actions.deleteSuccess());
  // Said out loud here, unlike on the card page, where being returned to the list is itself the answer.
  yield put(notifications.success(t('dash.admin.rateCards.notifications.delete_success')));
}

export default function* rootSaga() {
  yield all([
    takeLatest(actionTypes.LOAD_FORM_DATA_REQUEST, loadFormDataRequest),
    takeLatest(actionTypes.LOAD_DATA_REQUEST, loadDataRequest),
    // Not what stops a second DELETE — the component's `deleting` guard does that, and this cancels the
    // task, not the request in flight. What it buys is that only the last answer reaches the store.
    takeLatest(actionTypes.DELETE_REQUEST, deleteCardRequest),
  ]);
}
